import type { Job } from '@/api/endpoints/jobs'

/**
 * Os vídeos de um job, para o detalhe da tela de Jobs. Puro, para testar sem DOM.
 *
 * Duas origens:
 * - ``detail.videos``: o job de capa (e o de geração manual), com ``id``,
 *   ``language`` e ``title``;
 * - ``result.videos``: os ciclos do scheduler (geração e upload), com
 *   ``video_id``, ``publish_at`` e o desfecho. A API completa ``language``,
 *   ``title`` e ``channel_slug``; sem eles (vídeo apagado depois), o vídeo
 *   aparece sem link.
 */

export interface JobVideo {
  id: string
  /** ``null`` quando o projeto não existe mais: o vídeo aparece sem link. */
  language: string | null
  title: string
  /** Canal onde o projeto está agora; ``null`` se a API não mandou. */
  channel: string | null
  durationSeconds: number | null
  /** Só nos jobs ``studio_thumbnail``: o desfecho da capa daquele vídeo. */
  studioStatus: string | null
  /** Por onde a capa saiu: ``studio`` (desktop) ou ``app`` (celular, nos canais sem verificação). */
  via: string | null
  /** Quando o vídeo entra no ar (upload agendado); ``null`` fora do upload. */
  publishAt: string | null
  /** ``failed``, ``skipped``…; ``null`` quando o job não diz. */
  status: string | null
  error: string
}

function str(obj: Record<string, unknown>, key: string): string | null {
  const v = obj[key]
  return typeof v === 'string' && v ? v : null
}

function num(obj: Record<string, unknown>, key: string): number | null {
  const v = obj[key]
  return typeof v === 'number' ? v : null
}

function list(source: unknown, key: string): Record<string, unknown>[] {
  if (!source || typeof source !== 'object') return []
  const raw = (source as Record<string, unknown>)[key]
  if (!Array.isArray(raw)) return []
  return raw.filter((v): v is Record<string, unknown> => Boolean(v) && typeof v === 'object')
}

function fromDetail(obj: Record<string, unknown>): JobVideo | null {
  const id = str(obj, 'id')
  const language = str(obj, 'language')
  if (!id || !language) return null
  return {
    id,
    language,
    title: str(obj, 'title') ?? '',
    channel: str(obj, 'channel_slug'),
    durationSeconds: num(obj, 'duration_seconds'),
    studioStatus: str(obj, 'studio_status'),
    via: str(obj, 'via'),
    publishAt: null,
    status: null,
    error: str(obj, 'error') ?? '',
  }
}

function fromResult(obj: Record<string, unknown>): JobVideo | null {
  // Vaga que falhou antes de gerar não tem vídeo: o motivo já está no job.
  const id = str(obj, 'video_id')
  if (!id) return null
  return {
    id,
    language: str(obj, 'language'),
    title: str(obj, 'title') ?? '',
    channel: str(obj, 'channel_slug'),
    durationSeconds: num(obj, 'duration_seconds'),
    studioStatus: null,
    via: null,
    publishAt: str(obj, 'publish_at'),
    status: str(obj, 'status'),
    error: str(obj, 'error') ?? '',
  }
}

export function extractJobVideos(job: Job): JobVideo[] {
  const detail = list(job.detail, 'videos')
    .map(fromDetail)
    .filter((v): v is JobVideo => v !== null)
  if (detail.length > 0) return detail
  return list(job.result, 'videos')
    .map(fromResult)
    .filter((v): v is JobVideo => v !== null)
}

/** "Canal Um", ou "Pool Virais" quando o job é de um pool. */
export function jobChannelLabel(job: Pick<Job, 'channel_name' | 'channel_kind'>): string | null {
  if (!job.channel_name) return null
  return job.channel_kind === 'pool' ? `Pool ${job.channel_name}` : job.channel_name
}

/** "entra hoje 22:00", "entra 17/10 08:00"; vazio sem horário. */
export function publishLabel(publishAt: string | null, now: Date): string {
  if (!publishAt) return ''
  const at = new Date(publishAt)
  if (Number.isNaN(at.getTime())) return ''
  const hh = String(at.getHours()).padStart(2, '0')
  const mm = String(at.getMinutes()).padStart(2, '0')
  const sameDay = at.toDateString() === now.toDateString()
  const day = sameDay ? 'hoje' : `${String(at.getDate()).padStart(2, '0')}/${String(at.getMonth() + 1).padStart(2, '0')}`
  const verb = at.getTime() > now.getTime() ? 'entra' : 'entrou'
  return `${verb} ${day} ${hh}:${mm}`
}

/** Clique na linha do job abre o detalhe, menos em link, botão ou campo dentro dela. */
export function isRowToggleClick(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return true
  return !target.closest('a, button, input, select, textarea, [role="button"]')
}
